import { centsToPayPalAmount } from '@apokto/commerce-utils';
import { StandardEvents } from '@shopify/events';

class ApoktoPayPalMessage extends HTMLElement {
  static observedAttributes = ['data-amount-cents'];

  connectedCallback() {
    if (this.controller) return;
    this.renderAttempts = new WeakSet();
    this.currentTarget = null;
    this.controller = new AbortController();
    this.section = this.closest('.shopify-section, dialog');
    if (this.dataset.context === 'product') {
      this.section?.addEventListener(StandardEvents.productSelect, this.onProductSelect, {
        signal: this.controller.signal,
      });
    }
    this.observer = new MutationObserver(this.onMutation);
    this.observer.observe(this, { childList: true, subtree: true });
    this.syncTarget({ allowNewTargetRender: document.readyState === 'complete' });
    window.addEventListener('load', this.onWindowLoad, { once: true, signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    clearTimeout(this.renderTimer);
    this.controller = null;
    this.observer = null;
    this.currentTarget = null;
  }

  attributeChangedCallback() {
    if (this.isConnected) this.syncAmount();
  }

  onWindowLoad = () => this.syncTarget();

  onMutation = () => this.syncTarget({ allowNewTargetRender: document.readyState === 'complete' });

  onProductSelect = (event) => {
    event.promise?.then(({ detail }) => {
      const price = Number(detail?.resource?.price);
      if (Number.isFinite(price) && price > 0) this.dataset.amountCents = String(price);
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[apokto-paypal] Variant update failed:', error);
    });
  };

  syncAmount(target = this.querySelector('[data-apokto-paypal-target]')) {
    if (!target) return '';
    const amount = centsToPayPalAmount(this.dataset.amountCents);
    if (amount) target.dataset.ppAmount = amount;
    return amount;
  }

  syncTarget({ allowNewTargetRender = false } = {}) {
    const target = this.querySelector('[data-apokto-paypal-target]');
    if (!target || !this.syncAmount(target)) {
      this.currentTarget = target;
      this.classList.remove('is-ready');
      return;
    }

    const isNewTarget = target !== this.currentTarget;
    this.currentTarget = target;
    this.updateReadiness(target);
    if (allowNewTargetRender && isNewTarget && target.childElementCount === 0) {
      this.scheduleNewTargetRender(target);
    }
  }

  updateReadiness(target) {
    this.classList.toggle('is-ready', target === this.currentTarget && target.childElementCount > 0);
  }

  scheduleNewTargetRender(target) {
    if (this.renderAttempts.has(target) || !window.PayPalSDK?.Messages) return;
    this.renderAttempts.add(target);
    clearTimeout(this.renderTimer);
    this.renderTimer = setTimeout(async () => {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (
        !this.isConnected ||
        !target.isConnected ||
        this.querySelector('[data-apokto-paypal-target]') !== target ||
        target.childElementCount > 0
      ) {
        return;
      }

      const amount = this.syncAmount(target);
      if (!amount) return;
      try {
        const messages = window.PayPalSDK.Messages({ amount });
        if (!messages) return;
        await messages.render(target);
        if (this.isConnected && target.isConnected && this.currentTarget === target) {
          this.updateReadiness(target);
        }
      } catch (error) {
        if (this.isConnected && target.isConnected && this.currentTarget === target) {
          this.classList.remove('is-ready');
          console.warn('[apokto-paypal] Messaging unavailable:', error);
        }
      }
    }, 250);
  }
}

if (!customElements.get('apokto-paypal-message')) {
  customElements.define('apokto-paypal-message', ApoktoPayPalMessage);
}
